import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
  Container,
  Row,
  Col,
  Spinner,
  FormGroup,
  Label
} from "reactstrap";
import Calendar from '../ScheduleCalendar';
import Session from '../auth/Session';
import Select from 'react-select';

const ScheduleJobs = () => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const [jobs, setJobs] = useState([]);
  const [scheduledEvents, setScheduledEvents] = useState([]);
  const [equipmentOptions, setEquipmentOptions] = useState([]);

  const [crewOptions, setCrewOptions] = useState([]);
  const [selectedCrews, setSelectedCrews] = useState([]);
  const [assignToCrew, setAssignToCrew] = useState(null);

  const jobContainerRef = useRef(null);

  // Track the date range the calendar is currently showing so we can
  // reload events when the crew or visible week changes.
  const [visibleRange, setVisibleRange] = useState(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay()); // start of current week
    const end = new Date(start);
    end.setDate(start.getDate() + 7);
    return { start, end };
  });

  useEffect(() => {
    Promise.all([loadJobs(), getCrewsDropdown(), loadEquipment()]);
  }, []);

  // Reload scheduled events whenever selected crews or visible date range changes.
  useEffect(() => {
    if (selectedCrews.length === 0) {
      setScheduledEvents([]);
      return;
    }
    loadScheduledEvents(selectedCrews, visibleRange.start, visibleRange.end);
  }, [selectedCrews, visibleRange]);

  // -- Helpers

  const authHeaders = () => {
    const token = Session.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // -- Datetime helpers

  // Formats a JS Date as a local datetime string with NO UTC conversion.
  const toLocalISOString = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
           `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  // Converts a ScheduledEventDTO from the server into a FullCalendar event object.
  const dtoToEvent = (dto) => {
    const color = dto.crewColorHex || "#3788d8"; // fallback to FullCalendar default blue
    return {
      id: String(dto.id),
      title: dto.jobName,
      start: dto.start,
      end: dto.end,
      backgroundColor: color,
      borderColor: color,
      extendedProps: {
        dbId: dto.id,
        jobListingId: dto.jobListingId,
        jobName: dto.jobName,
        customerName: dto.customerName,
        schedulingNotes: dto.schedulingNotes || "",
        crewId: dto.crewId,
        crewName: dto.crewName,
        crewColor: color,
        equipmentIds: dto.equipmentIds || [],
        equipmentNames: dto.equipmentNames || [],
        materials: dto.materials || "",
        materialRequirements: dto.materialRequirements,
      },
    };
  };

  // -- Jobs
  const getJobs = async () => {
    const token = Session.getToken();
    const response = await fetch(`api/JobListing/GetAll`, {
      headers: !token ? {} : { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Failed to load job listings.");
    }
    return await response.json();
  };

  const loadJobs = async (showSpinner = true) => {
    try {
      if (showSpinner) setLoading(true);
      const data = await getJobs();
      const mapped = data.map((j) => ({
        id: j.id,
        name: j.jobName,
        customerName: j.customerName,
        equipmentIds: j.equipmentIds || [],
        equipmentNames: j.equipmentNames || [],
        materialRequirements: j.materialRequirements || "",
        schedulingNotes: j.schedulingNotes || "",
        manHours: j.manHours,
        remainingHours: j.remainingHours,
        jobClass: j.jobClass,
        isOpen: j.isOpen,
        isDeleted: j.isDeleted,
        approvedOn: j.approvedOn
      }));
      setJobs(mapped);
    } catch (e) {
      setError(e.message || "Failed to load jobs.");
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  // -- Scheduled Events

  const fetchEventsForCrew = async (crewOption, start, end, token) => {
    const params = new URLSearchParams({
      crewId: crewOption.value,
      start: toLocalISOString(start),
      end: toLocalISOString(end),
    });
    const response = await fetch(`api/ScheduledEvent/GetByCrewAndDateRange?${params}`, {
      headers: !token ? {} : { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Failed to load events for crew: ${crewOption.label}`);
    }
    return await response.json();
  };

  const loadScheduledEvents = async (crews, start, end) => {
    try {
      const token = Session.getToken();

      // Fire one request per crew in parallel.
      const results = await Promise.all(
        crews.map((crew) => fetchEventsForCrew(crew, start, end, token))
      );

      // Flatten all results - each dto carries its own crewColorHex from the server.
      const allEvents = results.flatMap((dtos) => dtos.map(dtoToEvent));

      setScheduledEvents(allEvents);
    } catch (e) {
      setError(e.message || "Failed to load scheduled events.");
    }
  };

  // -- Calendar API callbacks

  // Called when a job is dropped onto the calendar (eventReceive).
  const handleCreateEvent = useCallback(async (pendingEvent) => {
    try {
      const ep = pendingEvent.extendedProps || {};
      const start = pendingEvent.start instanceof Date
        ? pendingEvent.start
        : new Date(pendingEvent.start);

      const optimisticColor = assignToCrew?.color || "#3788d8";

      const payload = {
        jobListingId: ep.jobId,
        crewId: assignToCrew?.value,
        start: toLocalISOString(start),
        end: toLocalISOString(new Date(start.getTime() + 2 * 60 * 60 * 1000)),
        equipmentIds: ep.equipmentIds || [],
        materials: ep.materials || "",
      };

      const response = await fetch(`api/ScheduledEvent/CreateMulti`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to create events.");
      }

      const savedEvents = await response.json();

      setScheduledEvents((prev) =>
        prev
          .filter((e) => e.id !== pendingEvent.id)
          .concat(savedEvents.map(dtoToEvent))
      );

      // refresh job list / re-filter potential full jobs
      await loadJobs(false);

      return { ok: true, color: optimisticColor };
    } catch (e) {
      setError(e.message || "Failed to save events.");
      return { ok: false, message: e.message };
    }
}, [assignToCrew]);

  // Called when an event is dragged/resized OR when the modal saves
  const handleUpdateEvent = useCallback(async (eventId, patch) => {
    try {
      const existing = scheduledEvents.find((e) => e.id === String(eventId));
      if (!existing) throw new Error("Event not found in local state.");

      const ep = existing.extendedProps || {};
      const existingStart = existing.start instanceof Date
        ? existing.start
        : new Date(existing.start);
      const existingEnd = existing.end instanceof Date
        ? existing.end
        : new Date(existing.end);

      const payload = {
        id: ep.dbId,
        jobListingId: ep.jobListingId,
        crewId: ep.crewId,
        start: patch.start
          ? toLocalISOString(patch.start instanceof Date ? patch.start : new Date(patch.start))
          : toLocalISOString(existingStart),
        end: patch.end
          ? toLocalISOString(patch.end instanceof Date ? patch.end : new Date(patch.end))
          : toLocalISOString(existingEnd),
        equipmentIds: patch.equipmentIds ?? ep.equipmentIds ?? [],
        materials: patch.materials !== undefined ? patch.materials : ep.materials ?? "",
      };

      const response = await fetch(`api/ScheduledEvent/Update`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to update event.");
      }

      const saved = await response.json();

      // Swap the stale event for the fresh server-returned one.
      setScheduledEvents((prev) =>
        prev.map((e) => (e.id === String(eventId) ? dtoToEvent(saved) : e))
      );

      // Resizing/moving may update man hours - refresh the job list to re-filter
      await loadJobs(false);

      return { ok: true };
    } catch (e) {
      setError(e.message || "Failed to update event.");
      return { ok: false, message: e.message };
    }
  }, [scheduledEvents]);

  // Called when the modal deletes an event.
  const handleDeleteEvent = useCallback(async (eventId) => {
    try {
      const existing = scheduledEvents.find((e) => e.id === String(eventId));
      if (!existing) throw new Error("Event not found.");

      const dbId = existing.extendedProps?.dbId;
      const response = await fetch(`api/ScheduledEvent/Delete/${dbId}`, {
        method: "DELETE",
        headers: authHeaders(),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to delete event.");
      }

      setScheduledEvents((prev) => prev.filter((e) => e.id !== String(eventId)));

      // Deleting frees man-hours - jobs may now reappear in the list.
      await loadJobs(false);

      return { ok: true };
    } catch (e) {
      setError(e.message || "Failed to delete event.");
      return { ok: false, message: e.message };
    }
  }, [scheduledEvents]);

  // -- Equipment

  const getEquipment = async () => {
    const token = Session.getToken();
    const response = await fetch(`api/Equipment/GetAll`, {
      headers: !token ? {} : { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || "Failed to load equipment.");
    }
    return await response.json();
  };

  const loadEquipment = async () => {
    try {
      const data = await getEquipment();
      const options = data
        .filter((e) => e.isActive !== false 
            && e.category !== "Vehicle" 
            && e.category !== "Trailer")
        .map((e) => ({ value: e.id, label: e.equipmentName }));
      setEquipmentOptions(options);
    } catch (e) {
      console.error("Failed to load equipment:", e.message);
    }
  };

  // -- Crews

  const getCrewsDropdown = async () => {
    setLoading(true);
    const token = Session.getToken();
    const response = await fetch(`api/Crew/GetAll?activeOnly=true`, {
      headers: !token ? {} : { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      console.error("Get Crews failed", response.status);
      setCrewOptions([]);
      return;
    }
    const crews = await response.json();
    setCrewOptions(crews.map((c) => ({ value: c.id, label: c.name, color: c.colorHex })));
    setLoading(false);
  };

  // Selecting a crew here drives what new dropped events are assigned to.
  // It also auto-adds that crew to the "View Schedule" multi-select so their
  // existing events appear on the calendar immediately.
  const handleAssignCrewChange = (val) => {
    setAssignToCrew(val || null);
    if (val && !selectedCrews.find((c) => c.value === val.value)) {
      setSelectedCrews((prev) => [...prev, val]);
    }
  };

  // -- Styles

  const customSelectStyles = {
    dropdownIndicator: (base) => ({
      ...base,
      color: "#272727ff",
      "&:hover": { color: "#272727ff" },
    }),
  };

  return (
    <div>
      {error && (
        <Row>
          <Col>
            <div className="alert alert-danger alert-dismissible mb-0" role="alert">
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError("")}
                aria-label="Close"
              />
            </div>
          </Col>
        </Row>
      )}

      <Container className="container-main">
        {loading ? (
          <div style={{ marginTop: "2rem", textAlign: "center" }}>
            <Spinner />
          </div>
        ) : (
          <Row style={{ marginTop: 20 }}>
            {/* Calendar */}
            <Col md="9">
              <Calendar
                events={scheduledEvents}
                setEvents={setScheduledEvents}
                equipmentOptions={equipmentOptions}
                onVisibleRangeChange={setVisibleRange}
                onCreate={handleCreateEvent}
                onUpdate={handleUpdateEvent}
                onDelete={handleDeleteEvent}
                jobContainerRef={jobContainerRef}
                jobCount={jobs.length}
                onError={(msg) => setError(msg)}
              />
            </Col>

            {/* Sidebar */}
            <Col md="3" style={{ marginTop: "3.5rem", height: "100%" }}>

              {/* View Schedule for Crew(s) */}
              <div className="border p-3 rounded bg-light mb-4">
                <FormGroup>
                  <Label>View Schedule for Crew(s): </Label>
                  <Select
                    styles={customSelectStyles}
                    placeholder="Assigned crews"
                    options={crewOptions}
                    value={selectedCrews}
                    onChange={(vals) => setSelectedCrews(vals || [])}
                    isClearable
                    isMulti
                  />
                </FormGroup>

              </div>

              {/* Assign Job to Crew + Job List */}
              <div className="border p-3 rounded bg-light">
                <FormGroup>
                  <Label>Assign Job To Crew: </Label>
                  <Select
                    styles={customSelectStyles}
                    placeholder="Select a crew..."
                    options={crewOptions}
                    value={assignToCrew}
                    onChange={handleAssignCrewChange}
                    isClearable
                  />
                </FormGroup>

                <hr />

                <h6>Jobs</h6>

                {!assignToCrew && (
                  <p style={{ fontSize: "0.8rem", color: "#888" }}>
                    Select a crew above to start scheduling jobs.
                  </p>
                )}

                <div ref={jobContainerRef} style={{ maxHeight: "350px", overflowY: "auto" }}>
                  {jobs
                    .filter(j => j.isOpen && !j.isDeleted && j.approvedOn !== null && j.remainingHours > 0)
                    .map((job) => (
                    <div
                      key={job.id}
                      className="fc-external-job p-2 mb-2 border rounded bg-white"
                      data-id={job.id}
                      data-name={job.name}
                      data-customer={job.customerName}
                      data-equipment-ids={JSON.stringify(job.equipmentIds)}
                      data-equipment-names={JSON.stringify(job.equipmentNames)}
                      data-material-requirements={job.materialRequirements}
                      data-scheduling-notes={job.schedulingNotes}
                      style={{
                        cursor: assignToCrew ? "grab" : "not-allowed",
                        opacity: assignToCrew ? 1 : 0.5,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>{job.name}</div>
                      {job.customerName && (
                        <div style={{ fontSize: "0.75rem", color: "#666" }}>{job.customerName}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ScheduleJobs;